import { UNASSIGNED_ROOM_LABEL } from './master-room.const';

/** The room shape shared by the list-filter DTOs (assets, tasks). */
export interface RoomFilterOptionSource {
  roomId: string;
  roomName: string;
  roomColor: string | null;
  isMasterRoom: boolean;
}

/** A room filter option representing every room sharing one case-insensitive name across apartments. */
export interface RoomFilterOption {
  /** Option value stored in the filter selection — the normalized room name. */
  value: string;
  /** Ids of all rooms merged under this option; the API receives them as `roomIds`. */
  roomIds: string[];
  roomName: string;
  roomColor: string | null;
  isMasterRoom: boolean;
}

const UNASSIGNED_ROOM_OPTION_VALUE = '__unassigned__';

/**
 * Merges rooms whose names differ only in letter case into a single option, so
 * one entry represents the same room across many apartments. Unnamed master
 * rooms collapse into one "unassigned" option.
 */
export function toMergedRoomOptions(rooms: readonly RoomFilterOptionSource[]): RoomFilterOption[] {
  const groups = new Map<string, RoomFilterOption>();
  for (const room of rooms) {
    const unassigned = room.isMasterRoom && !room.roomName;
    const value = unassigned ? UNASSIGNED_ROOM_OPTION_VALUE : room.roomName.trim().toLowerCase();
    const group = groups.get(value);
    if (group) {
      group.roomIds.push(room.roomId);
      group.isMasterRoom &&= room.isMasterRoom;
      group.roomColor ??= room.roomColor;
    } else {
      groups.set(value, {
        value,
        roomIds: [room.roomId],
        roomName: unassigned ? UNASSIGNED_ROOM_LABEL : room.roomName,
        roomColor: room.roomColor,
        isMasterRoom: room.isMasterRoom
      });
    }
  }
  return [...groups.values()];
}

/** Expands selected room option values to the room ids they represent. */
export function toSelectedRoomIds(selectedRooms: string[], roomOptions: RoomFilterOption[]): string[] {
  const selected = new Set(selectedRooms);
  return roomOptions.filter((option) => selected.has(option.value)).flatMap((option) => option.roomIds);
}
